import { AgGrid } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { DefaultColumnTypes } from "../../../../../utils/agGrid.constants";
import Money from "../../../../Money";
import { TextWithIcon } from "../../../../TextWithIcon";
import { TextWithTag } from "../../../../TextWithTag";
import { verticalCenterWrapper } from "../styles_vanilla.css";
import {
  INVALID_PAYMENT_INSTRUCTIONS_TOOLTIP_TEXT,
  PAYMENT_INSTRUCTIONS_UPDATED_TAG_TEXT,
  PAYMENT_INSTRUCTIONS_UPDATED_TOOLTIP_TEXT,
  PositionTabStatus,
} from "./constants";
import { PositionRowData } from "./types";
import { getPaymentMethodDisplay } from "./utils";

/**
 * List of the supported columns in the Select Position table
 * (ordered from left to right)
 */
export enum SupportedColumns {
  AccountName = "accountName",
  PaymentInstructions = "paymentInstructions",
  PaymentMethod = "paymentMethod",
  PaymentMethodStatus = "paymentMethodStatus",
  GrossAmount = "grossAmount",
  Adjustments = "adjustments",
  NetPayment = "netPayment",
}

/**
 * The mapper provides the corresponding column name displayed in the table
 */
export const columnNames = new Map<SupportedColumns, string>([
  [SupportedColumns.AccountName, "Account name"],
  [SupportedColumns.PaymentInstructions, "Payment instructions"],
  [SupportedColumns.PaymentMethod, "Payment method"],
  [SupportedColumns.PaymentMethodStatus, "Payment method status"],
  [SupportedColumns.GrossAmount, "Gross amount"],
  [SupportedColumns.Adjustments, "Adjustments"],
  [SupportedColumns.NetPayment, "Net payment"],
]);

export const getColumnDefinitions = (
  allowSelection: boolean,
  showStatusColumn: boolean,
): AgGrid.ColDef[] => [
  {
    field: SupportedColumns.AccountName,
    headerName: columnNames.get(SupportedColumns.AccountName),
    pinned: "left" as const,
    minWidth: 360,
    checkboxSelection: allowSelection,
    headerCheckboxSelection: allowSelection,
    cellRenderer: TextWithTag,
    cellClass: verticalCenterWrapper,
    cellRendererParams: ({ data }: { data: PositionRowData }) => {
      if (
        data.paymentInstructionsRecentlyUpdated &&
        data.positionTabStatus !== PositionTabStatus.Paid
      ) {
        return {
          label: data.accountName,
          tagLabel: PAYMENT_INSTRUCTIONS_UPDATED_TAG_TEXT,
          tagVariant: "warning",
        };
      }

      return {
        label: data.accountName,
      };
    },
    tooltipValueGetter: ({ data }: { data?: PositionRowData }) => {
      if (
        data?.paymentInstructionsRecentlyUpdated &&
        data?.positionTabStatus !== PositionTabStatus.Paid
      ) {
        return PAYMENT_INSTRUCTIONS_UPDATED_TOOLTIP_TEXT;
      }

      return "";
    },
  },
  {
    field: SupportedColumns.PaymentInstructions,
    headerName: columnNames.get(SupportedColumns.PaymentInstructions),
    width: 360,
  },
  {
    field: SupportedColumns.PaymentMethod,
    headerName: columnNames.get(SupportedColumns.PaymentMethod),
    maxWidth: 200,
    valueFormatter: ({ data }: { data: PositionRowData }) => {
      return getPaymentMethodDisplay(data.paymentMethod);
    },
  },
  {
    field: SupportedColumns.PaymentMethodStatus,
    headerName: columnNames.get(SupportedColumns.PaymentMethodStatus),
    hide: !showStatusColumn,
    minWidth: 300,
    cellRenderer: TextWithIcon,
    cellRendererParams: ({ data }: { data: PositionRowData }) => {
      return {
        label: data.unableToPayReason?.unableToPayReason || "",
        iconType: "exclamation-triangle",
        iconColor: Colors.Danger600,
      };
    },
    cellClass: verticalCenterWrapper,
    tooltipValueGetter: ({ data }: { data?: PositionRowData }) => {
      const invalidPaymentInstructionReason =
        data?.unableToPayReason?.invalidPaymentInstructionReason;
      return invalidPaymentInstructionReason
        ? INVALID_PAYMENT_INSTRUCTIONS_TOOLTIP_TEXT[invalidPaymentInstructionReason]
        : "";
    },
    lockVisible: true, // Prevents users from hiding/unhiding this column in UI
    // We don't want users to be able to show this column in the Available to Pay or Paid tables
  },
  {
    field: SupportedColumns.GrossAmount,
    headerName: columnNames.get(SupportedColumns.GrossAmount),
    type: DefaultColumnTypes.numericColumn,
    maxWidth: 200,
    sortable: false,
    cellRenderer: Money,
    cellRendererParams: ({ data }: { data: PositionRowData }) => {
      return {
        money: data.grossAmount,
        rounded: false,
      };
    },
  },
  {
    field: SupportedColumns.Adjustments,
    headerName: columnNames.get(SupportedColumns.Adjustments),
    type: DefaultColumnTypes.numericColumn,
    maxWidth: 200,
    sortable: false,
    cellRenderer: Money,
    cellRendererParams: ({ data }: { data: PositionRowData }) => {
      return {
        money: data.adjustments,
        rounded: false,
      };
    },
  },
  {
    field: SupportedColumns.NetPayment,
    headerName: columnNames.get(SupportedColumns.NetPayment),
    type: DefaultColumnTypes.numericColumn,
    maxWidth: 200,
    sortable: false,
    cellRenderer: Money,
    cellRendererParams: ({ data }: { data: PositionRowData }) => {
      return {
        money: data.netPayment,
        rounded: false,
      };
    },
  },
];
